import { useTranslation } from "react-i18next";
import { Box, Group, SimpleGrid, Text, TextInput } from "@mantine/core";
import { useSettings } from "../api/admin";
import type { Segment } from "../api/types";
import { formatDuration } from "../time/format";
import { msToZonedText, zonedToMs } from "../time/parse";
import type { ActiveZone } from "../time/useZone";
import { Chip } from "../ui/Chip";

/**
 * What each kind of segment is actually named by. A show has a billing, a place and the
 * station's marker; a jukebox track has an artist and a track name and nothing else. Showing
 * a track the show fields invited someone to type a city into a row that cannot have one.
 */
const SHOW_FIELDS = ["title", "djs", "guests", "city", "region", "country", "marker"] as const;
const TRACK_FIELDS = ["title", "artist"] as const;

type MetaField = (typeof SHOW_FIELDS)[number] | (typeof TRACK_FIELDS)[number];

const ALL_FIELDS: readonly MetaField[] = [...new Set([...SHOW_FIELDS, ...TRACK_FIELDS])];

// "State" rather than "Region": it is the station's own word. The column behind it is named
// region only because `state` on that table is already the review state.
//
// Catalogue keys rather than copy: a module-level constant translated at import would freeze
// the language (see `AdminApp.tsx`'s `labelKey`).
const LABEL_KEYS: Record<MetaField, string> = {
  title: "inspector.fieldTitle",
  djs: "inspector.fieldDjs",
  guests: "inspector.fieldGuests",
  artist: "inspector.fieldArtist",
  city: "inspector.fieldCity",
  region: "inspector.fieldRegion",
  country: "inspector.fieldCountry",
  marker: "inspector.fieldMarker",
};

export function fieldsFor(kind: Segment["kind"]): readonly MetaField[] {
  return kind === "track" ? TRACK_FIELDS : SHOW_FIELDS;
}

/** The text the boundary and metadata fields hold while they are being edited. */
export type Form = ReturnType<typeof toForm>;

/** The fields this kind of segment is named by, and the marker set behind the one that has one. */
export function MetaFields({
  segment,
  form,
  setForm,
}: {
  segment: Segment;
  form: Form;
  setForm: (edit: (current: Form) => Form) => void;
}) {
  const { t } = useTranslation("admin");
  const markers = useMarkerSuggestions();

  const field = (name: MetaField) => (
    <TextInput
      key={name}
      mb={10}
      fz={12}
      c="dimmed"
      label={t(LABEL_KEYS[name])}
      value={form[name]}
      // The marker set is editable in settings, so the suggestions are read from
      // there rather than hard-coded; it stays a text input because a DJ can and
      // does type something the station has not standardised yet. A native datalist
      // rather than `Autocomplete` for exactly that reason: the list is a hint, not
      // the set of allowed answers.
      list={name === "marker" ? "segment-markers" : undefined}
      onChange={(event) => setForm((c) => ({ ...c, [name]: event.target.value }))}
    />
  );
  if (segment.kind === "track") {
    return (
      <>
        {field("title")}
        {field("artist")}
      </>
    );
  }
  return (
    <>
      {field("title")}
      <SimpleGrid cols={{ base: 1, "360px": 2 }} type="container" spacing="xs">
        {field("djs")}
        {field("guests")}
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, "360px": 3 }} type="container" spacing="xs">
        {field("city")}
        {field("region")}
        {field("country")}
      </SimpleGrid>
      {field("marker")}
      <datalist id="segment-markers">
        {markers.map((marker) => (
          <option key={marker} value={marker} aria-label={marker} />
        ))}
      </datalist>
    </>
  );
}

/** Both boundaries, what they add up to, and the nudges that move them by the second. */
export function SegmentTimes({
  segment,
  form,
  setForm,
  zone,
  startMs,
  endMs,
}: {
  segment: Segment;
  form: Form;
  setForm: (edit: (current: Form) => Form) => void;
  zone: ActiveZone;
  startMs: number | null;
  endMs: number | null;
}) {
  const { t } = useTranslation("admin");
  const exported = segment.state === "exported";

  const nudge = (field: "start" | "end", seconds: number) => {
    const base = field === "start" ? startMs : endMs;
    if (base === null) return;
    setForm((current) => ({
      ...current,
      [field]: msToZonedText(base + seconds * 1000, zone.name),
    }));
  };

  return (
    <>
      {/* Numeric entry is not a convenience. At seven-day zoom one pixel is minutes, so
          dragging alone cannot express a boundary to the second. */}
      <TimeField
        label={t("inspector.startLabel", { zone: zone.short })}
        value={form.start}
        valid={startMs !== null}
        disabled={exported}
        onChange={(value) => setForm((c) => ({ ...c, start: value }))}
        onNudge={(seconds) => nudge("start", seconds)}
      />
      <TimeField
        label={t("inspector.endLabel", { zone: zone.short })}
        value={form.end}
        valid={endMs !== null}
        // A running show ends at the live edge, and the segmenter moves that end forward
        // every pass. The server refuses the field outright; this is what stops the panel
        // offering an edit that can only be wrong.
        disabled={exported || segment.on_air}
        onChange={(value) => setForm((c) => ({ ...c, end: value }))}
        onNudge={(seconds) => nudge("end", seconds)}
      />
      {segment.on_air && (
        <Text size="xs" c="dimmed" mt={-6} mb={10}>
          {t("inspector.liveEndLocked")}
        </Text>
      )}

      <Text size="xs" c="dimmed" mt={4} mb={10} style={{ fontVariantNumeric: "tabular-nums" }}>
        {startMs !== null && endMs !== null && endMs > startMs
          ? formatDuration((endMs - startMs) / 1000)
          : t("inspector.endBeforeStart")}
        {segment.edited && (
          <Text component="span" fz="inherit" c="warn">
            {" "}
            · {t("inspector.edited")}
          </Text>
        )}
      </Text>
    </>
  );
}

function TimeField({
  label,
  value,
  valid,
  disabled,
  onChange,
  onNudge,
}: {
  label: string;
  value: string;
  valid: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onNudge: (seconds: number) => void;
}) {
  const { t } = useTranslation("admin");
  return (
    <Box mb={10} fz={12} c="dimmed">
      <Box component="span" display="block" mb={4}>
        {label}
      </Box>
      <Group gap={4} align="center">
        <TextInput
          style={{ flex: "1 1 20ch" }}
          miw="min(100%, 20ch)"
          aria-label={label}
          styles={{ input: { fontVariantNumeric: "tabular-nums" } }}
          // `true` rather than a message: the field is its own explanation, and a line of
          // error copy appearing under it would shove the nudges around mid-edit.
          error={valid ? undefined : true}
          value={value}
          disabled={disabled}
          spellCheck={false}
          placeholder={t("inspector.timePlaceholder")}
          onChange={(event) => onChange(event.target.value)}
        />
        {/* Coarse-to-fine nudges, because the common correction after a drag is a few
            seconds and retyping the whole field to change one of them is absurd. */}
        <Group gap={4}>
          {[-60, -10, -1, 1, 10, 60].map((seconds) => (
            <Chip
              key={seconds}
              disabled={disabled}
              aria-label={
                seconds > 0
                  ? t("inspector.nudgeLater", { count: seconds })
                  : t("inspector.nudgeEarlier", { count: Math.abs(seconds) })
              }
              onClick={() => onNudge(seconds)}
            >
              {seconds > 0 ? `+${seconds}` : seconds}
            </Chip>
          ))}
        </Group>
      </Group>
    </Box>
  );
}

/**
 * The marker strings offered as suggestions, from the same setting the segmenter classifies
 * with. Falling back to the built-in three keeps the field usable when the settings request
 * has not landed, or when an operator has emptied the list.
 */
function useMarkerSuggestions(): string[] {
  const { data } = useSettings();
  const value = data?.find((setting) => setting.key === "live_markers")?.value;
  const configured = Array.isArray(value) ? value.filter((entry) => typeof entry === "string") : [];
  return configured.length ? configured : ["LIVE from", "LIVE PARTY from", "RECORDED at"];
}

export function toForm(segment: Segment, zone: string) {
  return {
    // Every field, not only the visible ones: the kind decides what is rendered, and a form
    // keyed on the kind would go stale if the segment's kind ever changed underneath.
    ...(Object.fromEntries(ALL_FIELDS.map((name) => [name, segment[name] ?? ""])) as Record<
      MetaField,
      string
    >),
    start: msToZonedText(Date.parse(segment.start), zone),
    end: msToZonedText(Date.parse(segment.end), zone),
  };
}

/**
 * The boundaries a save carries: only an edge whose field no longer holds the text
 * :func:`toForm` seeded it with.
 *
 * Compared as text, not as the instant it parses to. A boundary is derived from audio frame
 * timing and carries microseconds, while this field speaks whole seconds -- so every row
 * would read as dirty, and a save meant to fix a title would rewind both edges to the second
 * along with it. That is how a show comes to start before the track in front of it has
 * finished, and nothing rejects it: the exclusion constraint is show-against-show, because a
 * track legitimately sits inside a show.
 */
export function editedEdges(
  segment: Pick<Segment, "start" | "end">,
  form: { start: string; end: string },
  zone: string,
): { start?: string; end?: string } {
  const edges: { start?: string; end?: string } = {};
  for (const edge of ["start", "end"] as const) {
    if (form[edge] === msToZonedText(Date.parse(segment[edge]), zone)) continue;
    const ms = zonedToMs(form[edge], zone);
    // An unparseable field is the caller's business: `timesValid` already blocks the save.
    if (ms !== null) edges[edge] = new Date(ms).toISOString();
  }
  return edges;
}
